import { captureUnexpectedError, getGraphQLDomainMessage, getGraphQLErrorCandidates } from '@/shared/lib';

const ORDER_ERROR_MESSAGES = {
  advances_changed: 'Авансы изменились. Сумма обновлена; проверьте её перед оплатой.',
  final_receipt_required: 'Для зачёта аванса нужен итоговый чек кассы локации.',
  advance_in_settlement: 'Аванс включён в незавершённый чек. Сначала завершите кассовую операцию.',
  order_version_conflict: 'Состав уже изменён. Обновите его и повторите действие.',
  order_source_stale: 'Запись изменилась. Обновите состав перед оплатой.',
  order_invalid_transition: 'Это действие недоступно в текущем состоянии.',
  order_idempotency_conflict: 'Повторный запрос отличается от исходного. Откройте оплату заново.',
  order_payment_invalid: 'Проверьте способы оплаты и введённые суммы.',
  order_loyalty_redemption_unavailable:
    'Оплата баллами сейчас недоступна. Обновите состав или выберите оплату без баллов.',
  order_loyalty_redemption_limit: 'Количество баллов превышает допустимый лимит для этой суммы.',
  order_loyalty_balance_insufficient: 'На балансе клиента недостаточно баллов. Обновите состав и проверьте баланс.',
  order_refund_invalid: 'Проверьте выбранные строки и распределение возврата.',
  order_refund_approval_required:
    'Возврат платежа из другой смены нужно подтвердить у руководителя. Запросите подтверждение заново.',
  order_invariant_violation: 'Не удалось подтвердить финансовые итоги. Обновите состав.',
  order_not_found: 'Запись или покупка не найдена либо недоступна.',
  booking_payment_delegated: 'Запись оплачивается отдельным документом. Откройте его из записи.',
  order_payment_held: 'По заказу выставлен счёт. Чтобы оплатить иначе, аннулируйте счёт.',
  order_refund_transfer_invalid: 'Проверьте дату перевода и номер платёжного поручения.',
  product_stock_insufficient: 'Товара не хватает на складе. Обновите витрину и повторите.',
  product_invariant_violation: 'Действие с товаром недоступно. Проверьте данные и повторите.',
  order_operation_failed: 'Не удалось выполнить операцию. Повторите позже.',
} as const;

export type OrderErrorCode = keyof typeof ORDER_ERROR_MESSAGES;
export type OrderErrorAction = 'refresh' | 'reopen' | 'none';
export type OrderErrorInfo = {
  code: OrderErrorCode | 'unknown';
  message: string;
  action: OrderErrorAction;
};

export type OrderOperationName =
  | 'CreateOrderFromBookingGroup'
  | 'CreateShopOrderDraft'
  | 'RefreshOrderDraft'
  | 'SettleOrder'
  | 'CancelOrder'
  | 'RefundOrder'
  | 'RequestOrderRefundApproval'
  | 'ResolveOrderRefundApproval';

export type SafeOrderOperationContext = {
  operation: OrderOperationName;
  orderId?: string;
  bookingGroupId?: string;
  refundId?: string;
};

/** Текст сервера точнее заготовки: в нём номер счёта или границы даты перевода. */
const SERVER_MESSAGE_CODES: ReadonlySet<OrderErrorCode> = new Set([
  'order_payment_held',
  'order_refund_transfer_invalid',
]);

const isOrderErrorCode = (value: unknown): value is OrderErrorCode =>
  typeof value === 'string' && Object.hasOwn(ORDER_ERROR_MESSAGES, value);

export const getOrderErrorCode = (error: unknown): OrderErrorCode | null => {
  for (const candidate of getGraphQLErrorCandidates(error)) {
    const code = candidate.extensions?.code;
    if (isOrderErrorCode(code)) {
      return code;
    }
  }

  return null;
};

export const mapOrderError = (error: unknown): OrderErrorInfo => {
  const code = getOrderErrorCode(error);

  if (!code) {
    return {
      code: 'unknown',
      message: 'Не удалось выполнить операцию. Повторите позже.',
      action: 'none',
    };
  }

  const action: OrderErrorAction =
    code === 'order_version_conflict' ||
    code === 'advances_changed' ||
    code === 'order_source_stale' ||
    code === 'order_invariant_violation' ||
    code === 'order_refund_approval_required'
      ? 'refresh'
      : code === 'order_idempotency_conflict'
        ? 'reopen'
        : 'none';

  const message = SERVER_MESSAGE_CODES.has(code)
    ? getGraphQLDomainMessage(error, ORDER_ERROR_MESSAGES[code])
    : ORDER_ERROR_MESSAGES[code];

  return { code, message, action };
};

/**
 * Белый список telemetry-контекста: в него нельзя передать суммы, payment
 * payload или customer snapshot. Hawk получает только имя операции и UUID.
 */
export const buildSafeOrderOperationContext = (
  operation: OrderOperationName,
  ids: Omit<SafeOrderOperationContext, 'operation'> = {},
): SafeOrderOperationContext => ({ operation, ...ids });

/** Неожиданные Order-ошибки, которые нужно репортить в мониторинг (остальные — ожидаемые доменные). */
export const isUnexpectedOrderError = (code: OrderErrorInfo['code']): boolean =>
  code === 'unknown' || code === 'order_invariant_violation' || code === 'order_operation_failed';

/**
 * Отправляет sanitized-телеметрию в Hawk только для неожиданных Order-ошибок.
 * Ожидаемые доменные коды (version conflict, stale source, validation) не шумят.
 */
export const reportOrderOperationError = (
  errorInfo: OrderErrorInfo,
  operation: OrderOperationName,
  ids: Omit<SafeOrderOperationContext, 'operation'> = {},
): void => {
  if (isUnexpectedOrderError(errorInfo.code)) {
    captureUnexpectedError('Unexpected Orders GraphQL operation failure', {
      ...buildSafeOrderOperationContext(operation, ids),
    });
  }
};
